import { Component, OnInit, Output, EventEmitter} from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Sidenavaction } from 'src/assets/js/application/sidenavaction';
import SessionModel from '../../services/session.model';
import { SessionService } from '../../services/session.service';
import PersonsModel from '../../services/persons.model';
import { PersonsService } from '../../services/persons.service';
import { NotificationService } from '../../services/notification.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
import {ToastrService} from 'ngx-toastr';
import { Subscription } from 'rxjs';
import FirebasePushNotificationsModel from 'src/app/services/PushNotification.model';
import { FirebaseNotificationsMessagingService } from 'src/app/services/FirebaseMessaging.service';
import { FirebasePushNotificationService } from 'src/app/services/PushNotification.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import { NavigationToggleService } from 'src/app/services/navigation-toggle.service';

declare var $: any;

/**
 * TopnavComponent
 * 
 * The TopnavComponent is the primary navigation header component for the AquCloud application.
 * It manages user authentication, notifications, business entity information, and navigation menu toggles.
 * This component handles session management, logout functionality, push notifications from Firebase,
 * and displays user profile and notification dropdowns.
 * 
 * @Component({
 *   selector: 'app-topnav',
 *   templateUrl: './topnav.component.html',
 *   styleUrls: ['./topnav.component.scss']
 * })
 * 
 * @example
 * <app-topnav></app-topnav>
 * 
 * @implements {OnInit}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-topnav',
  templateUrl: './topnav.component.html',
  styleUrls: ['./topnav.component.scss']
})
export class TopnavComponent implements OnInit {
  
  /**
   * Stores the current session information
   * @type {SessionModel}
   */
  public session: SessionModel;
  
  /**
   * Array of Person objects retrieved from the service
   * @type {Array<PersonsModel>}
   */
  public persons: Array<PersonsModel>;
  
  /**
   * Currently selected person object
   * @type {PersonsModel}
   */
  public person: PersonsModel;
  
  /**
   * Array of all business entities available to the user
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;
  
  /**
   * Currently selected business entity
   * @type {BusinessEntity}
   */
  public businessEntity: BusinessEntity;
  
  /**
   * Display name of the logged-in user
   * @type {string}
   */
  public LoginUserName: string;
  
  /**
   * User's login person type (e.g., 'EMPLOYEE', 'Farmer', 'Business Admin')
   * @type {string}
   */
  public loginPersonType: string;
  
  /**
   * Flag indicating if the main navigation is active
   * @type {boolean}
   */
  public ismainnav: boolean;
  
  /**
   * Business name associated with the logged-in user
   * @type {string}
   */
  public loginUserBusinessName: string;
  
  /**
   * Business icon/logo for the logged-in user's business entity
   * @type {string | ArrayBuffer}
   */
  public loginUserBusinessIcon: string | ArrayBuffer;
  
  /**
   * Flag to control visibility of the user profile dropdown menu
   * @type {boolean}
   * @default false
   */
  public isProfileOpen: boolean = false;
  
  /**
   * Flag to control visibility of the notifications dropdown menu
   * @type {boolean}
   * @default false
   */
  public isNotificationsOpen: boolean = false;
  
  /**
   * RxJS subscription for Firebase push notifications
   * @type {Subscription}
   * @private
   */
  private notificationSubscription: Subscription;
  
  /**
   * Array of all push notifications for the current user
   * Filtered to only show notifications with 'account' in the activity name
   * @type {Array<FirebasePushNotificationsModel>}
   * @default []
   */
  public notifications: Array<FirebasePushNotificationsModel> = [];
  
  /**
   * Array of read push notifications (status = 2)
   * @type {Array<FirebasePushNotificationsModel>}
   * @default []
   */
  public pushNotficationReadObjects: Array<FirebasePushNotificationsModel> = [];
  
  /**
   * Array of unread push notifications (status = 1)
   * @type {Array<FirebasePushNotificationsModel>}
   * @default []
   */
  public pushNotficationUnReadObjects: Array<FirebasePushNotificationsModel> = [];
  
  /**
   * Flag to control visibility of the navigation menu
   * @type {boolean}
   * @default false
   */
  public isNavOpen: boolean = false;
  
  /**
   * Output event emitter for toggling the side navigation menu
   * Emits when the menu toggle button is clicked
   * 
   * @type {EventEmitter<void>}
   * @example
   * (toggleMenu)="onMenuToggled()"
   */
  @Output() toggleMenu: EventEmitter<void> = new EventEmitter();
  
  /**
   * Route to redirect to after successful login
   * Retrieved from localStorage
   * @type {string}
   */
  redirectTo: string;

  /**
   * Constructor for TopnavComponent
   * 
   * Initializes all required services and subscribes to Firebase notification updates.
   * The constructor sets up a subscription to listen for incoming push notifications
   * and triggers notification status updates when new notifications arrive.
   * 
   * @constructor
   * @param {ToastrService} toastService - Service for displaying toast notifications
   * @param {Router} router - Angular router for navigation
   * @param {NotificationService} notificationService - Custom notification service
   * @param {SessionService} sessionService - Service for managing user sessions
   * @param {Sidenavaction} sidenavaction - Utility for side navigation actions
   * @param {PersonsService} personService - Service for managing person data
   * @param {BusinessEntityService} businessEntityService - Service for managing business entities
   * @param {FirebaseNotificationsMessagingService} firebaseMessagingService - Firebase messaging service for push notifications
   * @param {FirebasePushNotificationService} firebasePushNotificationService - Service for Firebase push notification operations
   * @param {LocationStrategy} locationStrategy - Angular location strategy for URL management
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history
   * @param {NavigationToggleService} navToggleService - Service for managing navigation menu toggle state
   */
  constructor(private toastService: ToastrService, public router: Router, public notificationService: NotificationService, public sessionService: SessionService, public sidenavaction: Sidenavaction, public personService: PersonsService,
    public businessEntityService: BusinessEntityService, private firebaseMessagingService: FirebaseNotificationsMessagingService, private firebasePushNotificationService: FirebasePushNotificationService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, private navToggleService: NavigationToggleService) {
      // Subscribe to the updateNotifications$ observable
      this.notificationSubscription = this.firebaseMessagingService.firebaseNotificationObject.subscribe(() => {
        this.updateNotificationStatus();
      });
  }

  /**
   * Component initialization method
   * 
   * Performs the following operations:
   * 1. Retrieves user login information from localStorage
   * 2. Fetches all push notifications filtered by 'account' activity name
   * 3. Separates notifications into read and unread collections
   * 4. Initializes Firebase push notification listening
   * 5. Loads the logged-in user's business entity and branding assets
   * 6. Downloads and caches the business icon from S3 bucket
   * 7. Fetches all inactive or marked-for-inactivation business entities
   * 
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @throws {Error} If there's an issue retrieving data or downloading files
   * 
   * @example
   * // This method is called automatically by Angular after component creation
   */
  public async ngOnInit() {
     // loginperson type details
     this.loginPersonType = localStorage.getItem('loginPersonType'); // login person type
     this.loginUserBusinessName = localStorage.getItem('loginBusinessName'); // login person Business name
     this.LoginUserName = localStorage.getItem('LoginUserName'); // login person name
     this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.redirectTo = localStorage.getItem('redirectAfterLogin');
    try {
      this.pushNotficationReadObjects = [];
      this.pushNotficationUnReadObjects = [];
      this.notifications = (await this.firebasePushNotificationService.getAllPushNotificationsByReceiverID()).filter((notification: FirebasePushNotificationsModel) => /account/i.test(notification.activityName));
      // Return the unread objects
      this.pushNotficationUnReadObjects = this.notifications.filter((notificationObj: FirebasePushNotificationsModel) => notificationObj.status == 1);
      // Return the read objects
      this.pushNotficationReadObjects = this.notifications.filter((notificationObj: FirebasePushNotificationsModel) => notificationObj.status == 2);
      // Listen for push notifications
      this.firebaseMessagingService.listenPushNotifications();
      // loginperson type details
      this.businessEntity = await this.businessEntityService.getOneByID(JSON.parse(localStorage.getItem('businessEntityById')).id);
      this.businessEntities = (await this.businessEntityService.getAllBusinessEntity()).filter((businessObject: BusinessEntity) => businessObject.businessCategory && businessObject.businessCategory.length > 0 && businessObject.businessCategory[0].businessCategoryID && businessObject.businessCategory[0].businessCategoryID  != '61890daa27c16c001eb0d9a8' && (businessObject.accountStatus == 'markedForInactivation' || businessObject.accountStatus == 'inactivated'));
      if (this.businessEntity) {
        if (this.businessEntity && this.businessEntity.branding_attachments && this.businessEntity.branding_attachments.length > 0 && this.businessEntity.branding_attachments[0] && this.businessEntity.branding_attachments[0].file_path) {
          let filePath = this.businessEntity.branding_attachments[0].file_path;
          const file = await this.businessEntityService.downloadFileFromS3Bucket(filePath);
          file.subscribe(fileBlob => {
            const readFile = new FileReader();
            readFile.readAsDataURL(fileBlob);
            readFile.onload = () => {
              this.loginUserBusinessIcon = readFile.result;
              if (this.loginUserBusinessIcon) {
                localStorage.setItem('loginBusinessIcon', filePath);
              }
            }
          });
        }
      }
      this.loginUserBusinessIcon = localStorage.getItem('loginBusinessIcon'); // login person Business logo
      this.ismainnav = true;
    } catch (error) {
      alert(error);
    }
  }

  /**
   * Handles user logout functionality
   * 
   * This method performs the following operations:
   * 1. Retrieves the current user ID from localStorage
   * 2. Calls the session logout API to terminate the server-side session
   * 3. Clears all localStorage data except 'BatchFeedData'
   * 4. Removes the 'isLoggedIn' flag
   * 5. Navigates the user back to the login page
   * 
   * @async
   * @method logout
   * @returns {Promise<void>}
   * @throws {Error} If logout operation fails, displays error toast message
   * 
   * @example
   * // Called when user clicks the logout button
   * await this.logout();
   */
  public async logout() {
    let userId = localStorage.userID;
    try {
      this.session = await this.sessionService.sessionLogout(userId);
      this.clearLocalStorageExcept();
      // localStorage.clear();
      localStorage.removeItem('isLoggedIn');
      this.router.navigate(['/login']);
    } catch (error) {
      this.toastService.error(this.session.message);
    }
  }

  /**
   * Clears localStorage entries while preserving the BatchFeedData
   * 
   * This method iterates through all localStorage entries in reverse order
   * and removes each key except 'BatchFeedData' which is preserved for later use.
   * Iterating in reverse prevents index shifts that would occur when removing items forward.
   * 
   * @method clearLocalStorageExcept
   * @returns {void}
   * 
   * @example
   * this.clearLocalStorageExcept(); // Clears all storage except BatchFeedData
   */
  public clearLocalStorageExcept() {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key !== 'BatchFeedData') {
        localStorage.removeItem(key);
      }
    }
  }

  /**
   * Adds 5 hours and 30 minutes to a given time string
   * 
   * Uses moment.js to parse the input time and add 330 minutes (5 hours 30 minutes)
   * to it. This is commonly used for timezone conversion or time offset adjustments.
   * 
   * @method add_5_30_hrs
   * @param {any} time - Time string to be adjusted (format: 'HH:mm')
   * @returns {string} Formatted time string in 12-hour format with AM/PM (e.g., '03:30 PM')
   * 
   * @example
   * const adjustedTime = this.add_5_30_hrs('10:00'); // Returns '03:30 PM'
   */
  public add_5_30_hrs = (time: any) => {
    return moment(time, 'HH:mm').add(330, 'minutes').format('hh:mm A');
  };

  /**
   * Shows the user profile dropdown menu by setting the profile open flag to true
   * 
   * @method showProfile
   * @returns {void}
   * 
   * @example
   * this.showProfile(); // Displays the profile dropdown
   */
  public showProfile() {
    this.isProfileOpen = true;
  }

  /**
   * Hides the user profile dropdown menu by setting the profile open flag to false
   * 
   * @method hideProfile
   * @returns {void}
   * 
   * @example
   * this.hideProfile(); // Hides the profile dropdown
   */
  public hideProfile() {
    this.isProfileOpen = false;
  }

  /**
   * Shows the notifications dropdown menu by setting the notifications open flag to true
   * 
   * @method showNotifications
   * @returns {void}
   * 
   * @example
   * this.showNotifications(); // Displays the notifications dropdown
   */
  public showNotifications() {
    this.isNotificationsOpen = true;
  }

  /**
   * Hides the notifications dropdown menu by setting the notifications open flag to false
   * 
   * @method hideNotifications
   * @returns {void}
   * 
   * @example
   * this.hideNotifications(); // Hides the notifications dropdown
   */
  public hideNotifications() {
    this.isNotificationsOpen = false;
  }

  /**
   * Navigates to the account closure screen based on user type
   * 
   * This method:
   * 1. Marks the notification as read (status = 2)
   * 2. Updates the notification in the Firebase database
   * 3. Notifies the messaging service to refresh notifications
   * 4. Closes the notifications dropdown
   * 5. Routes to the appropriate account closure page based on user's login person type
   * 
   * Routing behavior:
   * - EMPLOYEE: Routes to `/home/account-closure/businessID/{notificationObject.receiverBusinessID}`
   * - Farmer or Business Admin: Routes to `/home/account-closure`
   * - Other types: No action taken
   * 
   * @async
   * @method navigateToAccountClosureScreen
   * @param {FirebasePushNotificationsModel} notificationObject - The notification object containing receiver business ID and other details
   * @returns {Promise<void>}
   * 
   * @example
   * const notification = { id: '123', receiverBusinessID: 'biz456', status: 1 };
   * await this.navigateToAccountClosureScreen(notification);
   */
  public async navigateToAccountClosureScreen(notificationObject: FirebasePushNotificationsModel) {
    notificationObject.status = 2;
    await this.firebasePushNotificationService.updateOneByID(notificationObject.id, notificationObject);
    this.firebaseMessagingService.notifyUpdate(); // To update top nav component
    this.isNotificationsOpen = false;
    if (this.loginPersonType == 'EMPLOYEE') {
      this.router.navigate([`/home/account-closure/businessID/${notificationObject.receiverBusinessID}`]);
    } else if (['Farmer', 'Business Admin'].includes(this.loginPersonType)){
      this.router.navigate(['/home/account-closure']);
    } else {

    }
  }


  /**
   * Updates the notification status by fetching fresh notification data
   * 
   * This method performs the following operations:
   * 1. Fetches all push notifications for the current user
   * 2. Filters notifications to only include those with 'account' in the activity name
   * 3. Separates notifications into unread (status = 1) and read (status = 2) arrays
   * 4. For non-EMPLOYEE users, refreshes the current business entity data
   * 
   * This method is typically called when new push notifications are received
   * to keep the notification lists in sync with the backend.
   * 
   * @async
   * @method updateNotificationStatus
   * @returns {Promise<void>}
   * 
   * @example
   * await this.updateNotificationStatus(); // Refreshes notification data
   */
  public async updateNotificationStatus() {
    this.notifications = (await this.firebasePushNotificationService.getAllPushNotificationsByReceiverID()).filter((notification: FirebasePushNotificationsModel) => /account/i.test(notification.activityName));
    // Return the unread objects
    this.pushNotficationUnReadObjects = this.notifications.filter((notificationObj: FirebasePushNotificationsModel) => notificationObj.status == 1);
    // Return the read objects
    this.pushNotficationReadObjects = this.notifications.filter((notificationObj: FirebasePushNotificationsModel) => notificationObj.status == 2);
    if (this.loginPersonType != 'EMPLOYEE') {
      this.businessEntity = await this.businessEntityService.getOneByID(JSON.parse(localStorage.getItem('businessEntityById')).id);
    }
  }

  /**
   * Emits the toggleMenu event to the parent component
   * 
   * This method is called when the menu toggle button in the top navigation is clicked.
   * It emits the toggleMenu EventEmitter to notify the parent component that the menu
   * toggle button was clicked, allowing the parent to respond accordingly.
   * 
   * @method onToggleMenu
   * @returns {void}
   * 
   * @example
   * this.onToggleMenu(); // Emits the toggleMenu event
   */
  public onToggleMenu(){
    this.toggleMenu.emit(); // Emit the event when the button is clicked
  }

  /**
   * Toggles the navigation menu state through the NavigationToggleService
   * 
   * This method calls the toggleMenu() method on the NavigationToggleService
   * which manages the global state of the navigation menu visibility.
   * 
   * @method navigationToggleMenu
   * @returns {void}
   * 
   * @example
   * this.navigationToggleMenu(); // Toggles the navigation menu
   */
  public navigationToggleMenu() {
    this.navToggleService.toggleMenu();
  }

  /**
   * Angular OnDestroy lifecycle hook
   * 
   * Unsubscribes from the Firebase notification subscription to prevent memory leaks.
   * This is called automatically by Angular when the component is destroyed.
   * 
   * @method ngOnDestroy
   * @returns {void}
   * 
   * @example
   * // Called automatically by Angular when component is destroyed
   */
  public ngOnDestroy(): void {
    this.notificationSubscription.unsubscribe();
  }
}
